/**
 * Enllaços de pagament (Stripe, F5) — lògica pura del frontend (`plans/stripe-payment-links.md`
 * §3.2 i §7). Res d'aquí toca la xarxa ni React: es pot provar sol.
 *
 * Els imports viatgen en **enters de cèntims**. La pantalla ensenya euros, i la conversió es fa
 * sobre el text (mai `Number(x) * 100`, que amb 68.2 dona 6820.000000000001).
 */

import { format, isValid } from 'date-fns';
import { z } from 'zod';
import type {
  CreatePaymentOrderInput,
  PaymentOrderPreview,
  PaymentOrderCheck,
  PaymentOrderCheckIssue,
  PaymentOrderErrorCode,
  PaymentOrderPreviewReservation,
  PaymentOrderSentVia,
  PaymentOrderView,
  PaymentOrderWarning,
  PaymentProposalLine,
  ReservationPaymentOutstanding,
} from '@guilleumes/types';
import { formatCalendarDate, langToLocale } from '@/lib/dates';

const has = (object: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(object, key);

// ─── Cèntims ↔ euros ─────────────────────────────────────────────────────────

/** Fins a 7 xifres d'euros i, si n'hi ha, 1 o 2 decimals. Coma o punt. */
const EURO_TEXT = /^(\d{0,7})(?:[.,](\d{1,2}))?$/;

/**
 * «68,2» → 6820 · «33.33» → 3333 · «0.3» → 30 · «12» → 1200. `null` si no és un import
 * (buit, lletres, més de dos decimals, negatiu). Només matemàtica d'enters.
 */
export function eurosTextToCents(text: string | null | undefined): number | null {
  const clean = (text ?? '').trim().replace(/\s|€/g, '');
  const match = EURO_TEXT.exec(clean);
  if (!match || (match[1] === '' && match[2] === undefined)) return null;
  const euros = match[1] === '' ? 0 : parseInt(match[1], 10);
  const decimals = (match[2] ?? '').padEnd(2, '0');
  return euros * 100 + (decimals === '' ? 0 : parseInt(decimals, 10));
}

/** 6820 → «68,20» (el text del camp; la coma, com s'escriu aquí). */
export function centsToEurosText(cents: number): string {
  const safe = Number.isInteger(cents) && cents > 0 ? cents : 0;
  return `${Math.trunc(safe / 100)},${String(safe % 100).padStart(2, '0')}`;
}

/** 6820 → «68,20 €», amb el format de l'idioma. Només per ensenyar. */
export function formatCents(cents: number, lang: string = 'ca'): string {
  return new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

// ─── Dates ───────────────────────────────────────────────────────────────────

/** Un instant ISO → «3 nov. 2026, 23:00» a l'hora del navegador. Sense data vàlida, `''`. */
export function formatDueAt(iso: string | null | undefined, lang: string = 'ca'): string {
  if (!iso) return '';
  const date = new Date(iso);
  return isValid(date) ? format(date, 'd MMM yyyy, HH:mm', { locale: langToLocale(lang) }) : '';
}

/** Un instant ISO → «11 oct.» */
export function formatShortDay(iso: string | null | undefined, lang: string = 'ca'): string {
  if (!iso) return '';
  const date = new Date(iso);
  return isValid(date) ? format(date, 'd MMM', { locale: langToLocale(lang) }) : '';
}

/** Un instant ISO → el valor d'un `<input type="datetime-local">` (hora del navegador). */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return isValid(date) ? format(date, "yyyy-MM-dd'T'HH:mm") : '';
}

/** El valor d'un `datetime-local` → ISO (UTC). `null` si no és cap data. */
export function localInputToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return isValid(date) ? date.toISOString() : null;
}

// ─── Sol·licitud vigent ──────────────────────────────────────────────────────

/** La sol·licitud oberta d'una reserva (la més nova): la que el popover ensenya. */
export function pickOpenOrder(
  orders: readonly PaymentOrderView[] | null | undefined,
): PaymentOrderView | null {
  return (orders ?? []).find((o) => o.status === 'OPEN') ?? null;
}

export const SENT_VIA_LABELS: Record<PaymentOrderSentVia, string> = {
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Correu',
  OTHER: 'Altres',
};

export const SENT_VIAS: readonly PaymentOrderSentVia[] = ['WHATSAPP', 'EMAIL', 'OTHER'];

/**
 * Un sol botó d'«enviat» (decisió del 14-OCT-2026): «Marca com enviat» apunta la sol·licitud i,
 * al backend i en la mateixa operació (`mark-sent`), el senyal «Link enviat» de la targeta del
 * calendari. Mentre hi ha un enllaç obert, el popover amaga l'antic «Link enviat».
 */
export const MARK_SENT_TITLE =
  "Apunta que l'enllaç s'ha enviat: a l'enllaç i a la targeta del calendari («Link enviat»)";

export function sentViaLabel(via: string | null | undefined): string {
  return via && via in SENT_VIA_LABELS ? SENT_VIA_LABELS[via as PaymentOrderSentVia] : (via ?? '');
}

export const ORDER_STATUS_LABELS: Record<PaymentOrderView['status'], string> = {
  DRAFT: 'Esborrany',
  OPEN: 'Obert',
  PAID: 'Pagat',
  EXPIRED: 'Caducat',
  CANCELLED: 'Cancel·lat',
  SUPERSEDED: 'Substituït',
  PARTIALLY_REFUNDED: 'Retornat en part',
  REFUNDED: 'Retornat',
  DISPUTED: 'En disputa',
};

/** L'estat d'un cobrament (`SUCCEEDED` → «Cobrat»). Viu aquí (pur) perquè el fan servir el
 *  detall de l'enllaç del popover i la pantalla de cobraments (`payment-charges.ts`). */
export const CHARGE_STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: 'Cobrat',
  PENDING: 'Pendent de confirmar',
  FAILED: 'Fallit',
  /** No és en euros (o l'import no és vàlid): es desa però no compta. */
  NOT_BOOKED: 'No comptat',
};

export const chargeStatusLabel = (status: string): string =>
  has(CHARGE_STATUS_LABELS, status) ? CHARGE_STATUS_LABELS[status] : status;

/** Noms curts dels mètodes de Stripe (el detall dels mètodes és a Configuració → Stripe). */
const METHOD_SHORT_LABELS: Record<string, string> = {
  card: 'Targeta',
  link: 'Link',
  bizum: 'Bizum',
  paypal: 'PayPal',
  revolut_pay: 'Revolut Pay',
  amazon_pay: 'Amazon Pay',
  sepa_debit: 'SEPA',
  customer_balance: 'Transferència',
  klarna: 'Klarna',
  multibanco: 'Multibanco',
};

export const methodLabel = (method: string | null | undefined): string =>
  !method ? '—' : has(METHOD_SHORT_LABELS, method) ? METHOD_SHORT_LABELS[method] : method;

/** Els estats en què ja hi ha hagut un cobrament: el popover n'ensenya una línia amb l'import. */
const SETTLED_STATUSES: readonly PaymentOrderView['status'][] = [
  'PAID',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'DISPUTED',
];

/**
 * La sol·licitud que el popover ensenya: l'oberta si n'hi ha; si no, la MÉS NOVA quan és caducada
 * (per tornar-la a demanar), cobrada/retornada/en disputa (estat i import) o tancada però «per
 * revisar». Una cancel·lada o substituïda sense res a mirar no s'ensenya: només «Crear enllaç».
 */
export function pickDisplayOrder(
  orders: readonly PaymentOrderView[] | null | undefined,
): PaymentOrderView | null {
  const open = pickOpenOrder(orders);
  if (open) return open;
  const list = orders ?? [];
  if (list.length === 0) return null;
  // L'API les dona de la més nova a la més vella; en empat de data, guanya la primera.
  const newest = list.reduce((best, o) => (o.createdAt > best.createdAt ? o : best), list[0]);
  if (newest.status === 'EXPIRED' || SETTLED_STATUSES.includes(newest.status)) return newest;
  return newest.needsReviewAt && newest.status !== 'DRAFT' ? newest : null;
}

// ─── El pendent del popover ──────────────────────────────────────────────────

export interface PopoverPaymentAmountsInput {
  /** `balance` de la reserva, en euros: el que diu Beds24. */
  balance: number | null | undefined;
  /** `isPaid` de la cel·la del calendari. */
  isPaid: boolean | null | undefined;
  /** `paymentStatusRaw` de la cel·la (`PENDING`, `PARTIAL`, `PAID`, `REFUNDED`). */
  paymentStatusRaw: string | null | undefined;
  /** El llibre del PMS (`GET /payments/orders/outstanding`), si s'ha llegit. */
  outstanding: ReservationPaymentOutstanding | null | undefined;
}

export interface PopoverPaymentAmounts {
  /** D'on surt el pendent: el llibre del PMS (hi ha cobraments per enllaç) o Beds24. */
  source: 'PMS_LEDGER' | 'BEDS24';
  /** El «Pendent: X€» del costat de «Pagament:», en euros; `null`: no se n'ensenya cap. */
  pendingEuros: number | null;
  /** El sufix de la línia «Import»: `true` «(pagat)», `false` «(pendent)», `null` cap. */
  isPaid: boolean | null;
  /**
   * El pendent surt del llibre i hi ha pagaments fora de Stripe que el llibre no veu (un parcial
   * posat a mà): el popover hi posa {@link OUTSIDE_STRIPE_WARNING}.
   */
  outsideStripeWarning: boolean;
}

/** L'avís quan hi ha pagaments fora de Stripe (decisió d'en Pere del 14-OCT-2026). */
export const OUTSIDE_STRIPE_WARNING = 'Hi ha pagaments fora de Stripe: el pendent pot ser menor';

/** El títol del «Pendent» quan surt del llibre del PMS. */
export const PMS_LEDGER_PENDING_TITLE =
  'Calculat amb els cobraments per enllaç del PMS (Beds24 no els veu): preu menys el cobrat net';

/**
 * El pendent que ensenya el popover (decisió d'en Pere del 14-OCT-2026). Els cobraments per enllaç
 * no s'apunten MAI a Beds24, i el `balance` de Beds24 no els veu: una reserva de 280 € amb un
 * enllaç de 50 € cobrat hi diu 280. Quan el llibre del PMS té cobraments per a la reserva, mana
 * el pendent del backend (el mateix que proposaria un enllaç nou); sense, el comportament de
 * sempre. Una reserva marcada `PAID` no ensenya pendent en cap dels dos casos. Si a més hi ha
 * pagaments fora de Stripe (`paymentsOutsideStripe`), el pendent del llibre va amb un avís.
 */
export function popoverPaymentAmounts(input: PopoverPaymentAmountsInput): PopoverPaymentAmounts {
  const markedPaid = input.paymentStatusRaw === 'PAID';
  const ledger = input.outstanding;
  if (ledger?.hasPmsPayments === true && ledger.outstandingCents !== null) {
    const pendingCents = markedPaid ? 0 : Math.max(0, ledger.outstandingCents);
    return {
      source: 'PMS_LEDGER',
      pendingEuros: pendingCents > 0 ? pendingCents / 100 : null,
      isPaid: pendingCents === 0,
      // Amb res pendent l'avís no diu res de nou.
      outsideStripeWarning: ledger.paymentsOutsideStripe === true && pendingCents > 0,
    };
  }
  const { balance } = input;
  return {
    source: 'BEDS24',
    pendingEuros: balance != null && balance > 0 && !markedPaid ? balance : null,
    isPaid: input.isPaid ?? null,
    outsideStripeWarning: false,
  };
}

/** Quant s'ha retornat dels cobraments d'una sol·licitud (cèntims). */
export function orderRefundedCents(order: PaymentOrderView): number {
  return (order.charges ?? []).reduce((sum, c) => sum + (c.refundedCents > 0 ? c.refundedCents : 0), 0);
}

// ─── «Per revisar» ───────────────────────────────────────────────────────────

/** Els codis de `reviewReason` que tenen una frase; la resta es pinten tal com arriben. */
const REVIEW_REASON_LABELS: Record<string, string> = {
  CHECKOUT_DEACTIVATION_FAILED: "no s'ha pogut tancar la sessió de pagament al proveïdor",
  CHECKOUT_COMPLETED_ON_CLOSED_ORDER:
    "l'enllaç ja estava pagat (o pagant-se) al proveïdor quan es va tancar: pot arribar un cobrament",
  RESERVATION_CANCELLED_IN_GROUP: "s'ha cancel·lat alguna habitació del grup",
  ORDER_AMOUNT_MISMATCH: "l'import de l'enllaç ja no és el pendent de la reserva",
  STAY_DATES_CHANGED: "les dates de l'estada han canviat",
  RESERVATION_MISSING: "una reserva de l'enllaç ja no existeix",
  ASSIGNED_EXCEEDS_OUTSTANDING:
    "s'hi ha assignat un cobrament més gran que el pendent de la reserva",
  PAID_AFTER_CANCELLED: "s'ha cobrat després de cancel·lar-lo",
  PAID_AFTER_EXPIRED: "s'ha cobrat després de caducar",
  PAID_AFTER_SUPERSEDED: "s'ha cobrat un enllaç substituït",
  OVERPAID: "s'ha cobrat de més",
  UNDERPAID: "s'ha cobrat de menys",
  PAID_RESERVATION_CANCELLED: "s'ha cobrat una reserva cancel·lada",
  DISPUTE_LOST: "s'ha perdut una disputa del cobrament",
  REFUNDED_CENTS_MISMATCH: "l'import retornat no quadra amb les devolucions apuntades",
  OUTSTANDING_UNKNOWN: 'no es pot calcular el pendent de la reserva (preu no vàlid)',
  RESERVATION_OVERPAID: 'la reserva té cobrat més que el seu preu, sumant tots els enllaços',
  CHECKOUT_CREATION_REJECTED:
    "Stripe ha rebutjat obrir el pagament (potser cap dels mètodes configurats és activat a Stripe): l'hoste no pot pagar",
  ORDER_HELD_UNRESOLVED:
    "fa hores que no es pot ni caducar ni apuntar-ne el cobrament: mira Stripe i torna'l a llegir",
};

/** Codis compostos (`<PREFIX>:<detall>`): frase genèrica i, darrere, el codi tal com arriba. */
const REVIEW_REASON_PREFIX_LABELS: Record<string, string> = {
  POLICY_UNDECIDED: "pendent d'una decisió sobre devolucions",
  RESERVATION_REVIEW: "l'estat de pagament de la reserva no és el que diu el llibre de cobraments",
  LEDGER: "el repartiment d'un cobrament o d'una devolució no és segur",
  TRANSITION_NOT_ALLOWED: "els fets demanen un canvi d'estat que no està previst",
};

/** Un codi de revisió → la seva frase. Un codi desconegut es torna tal com és. */
export function reviewReasonLabel(code: string): string {
  if (has(REVIEW_REASON_LABELS, code)) return REVIEW_REASON_LABELS[code];
  const colon = code.indexOf(':');
  const prefix = colon > 0 ? code.slice(0, colon) : '';
  return has(REVIEW_REASON_PREFIX_LABELS, prefix)
    ? `${REVIEW_REASON_PREFIX_LABELS[prefix]} (${code})`
    : code;
}

function splitReviewReasons(reason: string | readonly string[] | null | undefined): string[] {
  const codes = typeof reason === 'string' ? reason.split(',') : (reason ?? []);
  return codes.map((c) => String(c).trim()).filter(Boolean);
}

/** `reviewReason` (codis separats per comes, o ja partits) → una frase llegible. */
export function reviewReasonText(reason: string | readonly string[] | null | undefined): string {
  const codes = splitReviewReasons(reason);
  if (codes.length === 0) return 'sense motiu escrit';
  return codes.map(reviewReasonLabel).join(' · ');
}

/**
 * Els motius desats que diuen el mateix que `check`. En una sol·licitud oberta amb `check`, el que
 * es veu surt de la comprovació d'ARA: el motiu desat pot ser d'un desquadrament ja arreglat.
 */
const CHECK_REASON_CODES: readonly string[] = [
  'RESERVATION_CANCELLED_IN_GROUP',
  'ORDER_AMOUNT_MISMATCH',
  'STAY_DATES_CHANGED',
  'RESERVATION_MISSING',
  'OUTSTANDING_UNKNOWN',
];

/** La comprovació d'ara mana: sol·licitud oberta i el backend ha enviat `check`. */
function checkDrives(order: PaymentOrderView): boolean {
  return order.status === 'OPEN' && order.check != null;
}

/**
 * Els motius de revisió que s'ensenyen: `reviewReasons` (o `reviewReason` partit si el camp no
 * arriba). En una sol·licitud oberta amb `check`, sense els que la comprovació ja diu en viu.
 */
export function orderReviewReasons(order: PaymentOrderView): string[] {
  const codes = splitReviewReasons(
    Array.isArray(order.reviewReasons) ? order.reviewReasons : order.reviewReason,
  );
  return checkDrives(order) ? codes.filter((c) => !CHECK_REASON_CODES.includes(c)) : codes;
}

// ─── «L'enllaç demana X i la reserva val Y» ──────────────────────────────────

/** La comprovació d'una sol·licitud OBERTA que no quadra; `null` si quadra o no n'hi ha. */
export function orderCheckProblem(order: PaymentOrderView): PaymentOrderCheck | null {
  const check = order.status === 'OPEN' ? order.check : null;
  return check && check.ok === false ? check : null;
}

const CHECK_ISSUE_LABELS: Record<PaymentOrderCheckIssue, string> = {
  RESERVATION_CANCELLED: 'la reserva està cancel·lada',
  RESERVATION_CANCELLED_IN_GROUP: "s'ha cancel·lat una habitació del grup",
  RESERVATION_MISSING: "una reserva de l'enllaç ja no existeix",
  ORDER_AMOUNT_MISMATCH: "l'import ha canviat",
  STAY_DATES_CHANGED: "les dates de l'estada han canviat",
  OUTSTANDING_UNKNOWN: 'no es pot calcular el pendent de la reserva (preu no vàlid)',
};

export function checkIssueText(issue: string): string {
  return has(CHECK_ISSUE_LABELS, issue) ? CHECK_ISSUE_LABELS[issue as PaymentOrderCheckIssue] : issue;
}

/** Què no quadra, en paraules: «s'ha cancel·lat una habitació del grup; l'import ha canviat». */
export function checkIssuesText(check: PaymentOrderCheck): string {
  return (check.issues ?? []).map(checkIssueText).join('; ');
}

/** «L'enllaç demana 101,53 € i la reserva val 68,20 €.» (§3.4) */
export function orderCheckSentence(check: PaymentOrderCheck): string {
  if (check.outstandingCents == null) {
    return `L'enllaç demana ${formatCents(check.askedCents)} i ara no es pot calcular quant val la reserva.`;
  }
  const floor = check.policyDependent
    ? ' (com a mínim: depèn d’una decisió sobre devolucions pendent)'
    : '';
  return `L'enllaç demana ${formatCents(check.askedCents)} i la reserva val ${formatCents(check.outstandingCents)}${floor}.`;
}

/** Les línies que no quadren, una frase per reserva. Les que quadren no surten. */
export function orderCheckLineTexts(check: PaymentOrderCheck): { lineId: string; text: string }[] {
  return (check.lines ?? [])
    .filter(
      (l) =>
        l.amountMismatch ||
        l.cancelled ||
        l.datesChanged ||
        (l.checkIn === null && l.checkOut === null),
    )
    .map((l) => {
      const missing = l.checkIn === null && l.checkOut === null;
      const worth =
        l.outstandingCents == null ? 'no es pot calcular quant val' : `val ${formatCents(l.outstandingCents)}`;
      const notes: string[] = [];
      if (missing) notes.push('la reserva ja no existeix');
      if (l.cancelled) notes.push('cancel·lada');
      if (l.datesChanged) {
        const dates = [l.checkIn, l.checkOut]
          .map((d) => formatCalendarDate(d, 'ca', 'd MMM'))
          .filter(Boolean)
          .join(' – ');
        notes.push(dates ? `dates canviades (ara ${dates})` : 'dates canviades');
      }
      return {
        lineId: l.lineId,
        text: `Reserva ${l.beds24Id}: demana ${formatCents(l.askedCents)} i ${worth}${
          notes.length > 0 ? ` · ${notes.join(' · ')}` : ''
        }`,
      };
    });
}

export const PAST_DUE_TEXT = 'Ha passat la data màxima: aquest enllaç ja no es pot pagar.';

/** L'avís curt de la comprovació que no quadra (popover). `null` si quadra. */
export function orderCheckWarning(order: PaymentOrderView): string | null {
  const check = orderCheckProblem(order);
  if (!check) return null;
  const issues = checkIssuesText(check);
  return `${orderCheckSentence(check)}${issues ? ` Motiu: ${issues}.` : ''}`;
}

/**
 * L'avís d'una sol·licitud, si n'hi ha: data passada, l'import o les dates que ja no quadren
 * (comprovació d'ara) i «per revisar».
 */
export function orderWarning(order: PaymentOrderView): string | null {
  const parts: string[] = [];
  if (order.pastDue) parts.push(PAST_DUE_TEXT);
  const check = orderCheckWarning(order);
  if (check) parts.push(check);
  const reasons = orderReviewReasons(order);
  // Amb `check` manant i cap altre motiu, el «per revisar» desat ja no diu res.
  if (order.needsReviewAt && (reasons.length > 0 || !checkDrives(order))) {
    parts.push(`Per revisar: ${reviewReasonText(reasons)}.`);
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

// ─── Avisos d'una acció feta ─────────────────────────────────────────────────

export const ORDER_WARNING_TEXTS: Record<PaymentOrderWarning, string> = {
  CHECKOUT_DEACTIVATION_FAILED:
    "No s'ha pogut tancar la sessió de pagament de l'enllaç anterior al proveïdor: queda per revisar.",
  CHECKOUT_ALREADY_COMPLETED:
    "Compte: l'enllaç anterior ja estava pagat (o pagant-se) al proveïdor quan s'ha tancat. Pot arribar-ne el cobrament: queda per revisar. Mira-ho abans de demanar cap altre pagament a l'hoste.",
};

/** L'avís que cal mirar de seguida (vermell), a part dels altres. */
export const URGENT_ORDER_WARNING: PaymentOrderWarning = 'CHECKOUT_ALREADY_COMPLETED';

/** Els avisos d'una acció, en text; els codis que aquesta versió no coneix es pinten tal com són. */
export function orderWarningTexts(
  warnings: readonly string[] | null | undefined,
): { code: string; text: string; urgent: boolean }[] {
  return (warnings ?? []).map((code) => ({
    code,
    text: has(ORDER_WARNING_TEXTS, code) ? ORDER_WARNING_TEXTS[code as PaymentOrderWarning] : code,
    urgent: code === URGENT_ORDER_WARNING,
  }));
}

// ─── Errors de l'API ─────────────────────────────────────────────────────────

/** Per què està apagada la passarel·la (mateixos codis que `GATEWAY_OFF_EXPLANATIONS` del backend). */
const GATEWAY_OFF_LABELS: Record<string, string> = {
  NOT_CONFIGURED: 'no hi ha credencials de Stripe desades',
  MISSING_KEY: 'falta la clau secreta de Stripe',
  INVALID_KEY: 'la clau desada no té forma de clau de Stripe',
  LIVE_KEY_OUTSIDE_PRODUCTION: 'hi ha una clau de debò fora de producció (fre d’entorn)',
  TEST_KEY_IN_PRODUCTION: 'hi ha una clau de prova a producció (fre d’entorn)',
  CONFIG_UNAVAILABLE: "no s'ha pogut llegir la configuració",
  CLIENT_BUILD_FAILED: "el client de Stripe no s'ha pogut construir",
};

export function gatewayOffText(offReason: string | null | undefined): string {
  const detail = offReason ? (GATEWAY_OFF_LABELS[offReason] ?? offReason) : null;
  return detail
    ? `Els cobraments no estan configurats (${detail}). No es pot crear cap enllaç.`
    : 'Els cobraments no estan configurats. No es pot crear cap enllaç.';
}

const PROPOSAL_ERROR_LABELS: Record<string, string> = {
  NOTHING_OUTSTANDING: 'No hi ha res pendent de cobrar.',
  NO_LIVE_RESERVATIONS: 'Totes les reserves estan cancel·lades.',
  NO_RESERVATIONS: 'No hi ha cap reserva per cobrar.',
  EMPTY_SELECTION: 'Cal triar almenys una reserva.',
  SELECTED_CANCELLED: 'Una de les reserves triades està cancel·lada.',
  INVALID_PRICE: 'Alguna reserva té un preu que no es pot cobrar.',
  INVALID_DATES: 'Alguna reserva té les dates malament.',
  INVALID_LEDGER: 'El llibre de cobraments d’alguna reserva no quadra: cal mirar-ho abans.',
  INVALID_AMOUNT: "L'import no és vàlid.",
  AMOUNT_EXCEEDS_OUTSTANDING: "L'import passa del pendent.",
  DUE_IN_PAST: 'La data màxima ja ha passat.',
  MIXED_GROUPS: 'Les reserves no són del mateix grup.',
};

export function proposalErrorText(code: string | null | undefined): string {
  if (!code) return "No s'ha pogut preparar la proposta.";
  return PROPOSAL_ERROR_LABELS[code] ?? `No s'ha pogut preparar la proposta (${code}).`;
}

const EXCLUSION_LABELS: Record<string, string> = {
  CANCELLED: 'cancel·lada',
  NO_PRICE: 'sense preu',
  NOTHING_OUTSTANDING: 'res pendent',
  MANUALLY_PAID: 'marcada pagada a mà',
  MANUALLY_REFUNDED: 'marcada retornada a mà',
  NOT_SELECTED: 'no triada',
};

/** Els codis amb una frase fixa; els altres tres porten detall (`offReason`, `reason`). */
type PlainOrderErrorCode = Exclude<
  PaymentOrderErrorCode,
  'PAYMENTS_GATEWAY_OFF' | 'PROPOSAL_REJECTED' | 'TOTAL_CHANGED'
>;

/** Exhaustiu: un codi nou al contracte sense frase aquí fa fallar el typecheck. */
const ORDER_ERROR_LABELS: Record<PlainOrderErrorCode, string> = {
  RESERVATION_NOT_FOUND: "No s'ha trobat la reserva.",
  ORDER_NOT_FOUND: "No s'ha trobat l'enllaç de pagament.",
  LINE_NOT_ALLOWED: 'Una de les reserves no pot entrar a l’enllaç.',
  DUPLICATE_LINE: 'Hi ha una reserva repetida.',
  INVALID_LINE_AMOUNT: 'Un dels imports no és vàlid.',
  AMOUNT_BELOW_MINIMUM: "L'import total és massa petit per cobrar-lo amb targeta.",
  INVALID_DUE_DATE: 'La data màxima no és vàlida (ha de ser futura).',
  INVALID_TRANSITION: "L'enllaç ja no admet aquesta acció (ha canviat d'estat).",
  ORDER_CHANGED: "L'enllaç ha canviat mentre es feia l'acció: torna-ho a provar.",
  PAYMENT_IN_PROGRESS:
    "Hi ha un pagament fet o en curs amb l'enllaç anterior que encara no s'ha confirmat. Espera que es confirmi: ara no es crea ni es cancel·la res.",
  PROVIDER_UNAVAILABLE:
    "No s'ha pogut contactar amb el proveïdor de pagaments. Torna-ho a provar d'aquí a una estona.",
  ORDER_PAST_DUE:
    "L'enllaç ja ha passat la data màxima: no es pot marcar com enviat. Crea'n un de nou.",
  ORDER_CHECK_FAILED:
    "L'enllaç ja no quadra amb la reserva (import, dates o una habitació cancel·lada): no es pot marcar com enviat. Crea'n un de nou.",
};

/** `TOTAL_CHANGED`: `reason` porta el total d'ara, en cèntims. */
export function totalChangedText(reason: string | number | null | undefined): string {
  const text = reason == null ? '' : String(reason).trim();
  const now = /^\d{1,12}$/.test(text) ? ` Ara el total és ${formatCents(parseInt(text, 10))}.` : '';
  return `L'import ha canviat des que has obert el diàleg.${now} Revisa la proposta nova i torna a confirmar.`;
}

/** L'error demana tornar a llegir la proposta abans de tornar a confirmar. */
export function needsFreshPreview(error: unknown): boolean {
  return apiErrorCode(error) === 'TOTAL_CHANGED';
}

interface ApiErrorLike {
  message?: string;
  response?: {
    status?: number;
    data?: { code?: string; reason?: string; offReason?: string; message?: unknown };
  };
}

/** El `code` del cos d'error (`PaymentOrderErrorBody` o `{ code }` de les rutes públiques). */
export function apiErrorCode(error: unknown): string | null {
  const code = (error as ApiErrorLike | null)?.response?.data?.code;
  return typeof code === 'string' && code ? code : null;
}

export function apiErrorStatus(error: unknown): number | null {
  const status = (error as ApiErrorLike | null)?.response?.status;
  return typeof status === 'number' ? status : null;
}

/** La frase del toast d'un error de l'API d'ADMIN de sol·licituds. */
export function paymentOrderErrorText(error: unknown): string {
  const data = (error as ApiErrorLike | null)?.response?.data;
  const code = apiErrorCode(error);
  if (code === 'PAYMENTS_GATEWAY_OFF') return gatewayOffText(data?.offReason);
  if (code === 'PROPOSAL_REJECTED') return proposalErrorText(data?.reason);
  if (code === 'TOTAL_CHANGED') return totalChangedText(data?.reason);
  if (code && has(ORDER_ERROR_LABELS, code)) return ORDER_ERROR_LABELS[code as PlainOrderErrorCode];
  const message = (error as ApiErrorLike | null)?.message;
  return message ? `Error: ${message}` : 'Error inesperat';
}

// ─── Cancel·lar ──────────────────────────────────────────────────────────────

export const CANCEL_REASON_MAX = 200;

/** El motiu de cancel·lar: opcional, 200 caràcters com a molt (el mateix que valida el backend). */
export const cancelOrderSchema = z.object({
  reason: z.string().trim().max(CANCEL_REASON_MAX, `Màxim ${CANCEL_REASON_MAX} caràcters`),
});
export type CancelOrderFormValues = z.infer<typeof cancelOrderSchema>;

// ─── Diàleg de crear ─────────────────────────────────────────────────────────

/** Una fila del diàleg: una reserva del grup, amb la seva línia si es pot cobrar. */
export interface CreateOrderRow {
  reservation: PaymentOrderPreviewReservation;
  /** `null`: no es pot triar (cancel·lada, sense pendent…). */
  line: PaymentProposalLine | null;
  /** Per què no es pot triar, ja en text. `null` si es pot. */
  disabledReason: string | null;
}

export function buildCreateOrderRows(preview: PaymentOrderPreview): CreateOrderRow[] {
  const lines = new Map((preview.proposal?.lines ?? []).map((l) => [l.reservationId, l]));
  const excluded = new Map(
    (preview.proposal?.excluded ?? []).map((e) => [e.reservationId, e.reason as string]),
  );
  return preview.reservations.map((reservation) => {
    const line = lines.get(reservation.reservationId) ?? null;
    if (line) return { reservation, line, disabledReason: null };
    const code = !reservation.live ? 'CANCELLED' : excluded.get(reservation.reservationId);
    return {
      reservation,
      line: null,
      disabledReason: code ? (EXCLUSION_LABELS[code] ?? code) : 'no es pot cobrar',
    };
  });
}

export interface CreateOrderFormLine {
  reservationId: string;
  selected: boolean;
  /** Euros, tal com s'escriuen («68,20»). */
  amount: string;
}

export interface CreateOrderFormValues {
  lines: CreateOrderFormLine[];
  /** «Sense data màxima». */
  noDue: boolean;
  /** Valor del `datetime-local`. */
  dueAt: string;
}

/** Vives marcades, cadascuna pel seu pendent, i la data màxima per defecte. */
export function createOrderDefaults(preview: PaymentOrderPreview): CreateOrderFormValues {
  return {
    lines: buildCreateOrderRows(preview).map((row) => ({
      reservationId: row.reservation.reservationId,
      selected: row.line !== null,
      amount: row.line ? centsToEurosText(row.line.amountCents) : '',
    })),
    noDue: preview.proposal ? preview.proposal.dueAt === null : false,
    dueAt: isoToLocalInput(preview.proposal?.dueAt),
  };
}

/** Suma dels imports vàlids de les línies marcades (les que no es poden llegir compten 0). */
export function selectedTotalCents(lines: readonly CreateOrderFormLine[] | undefined): number {
  return (lines ?? []).reduce(
    (sum, l) => (l.selected ? sum + (eurosTextToCents(l.amount) ?? 0) : sum),
    0,
  );
}

/**
 * Sostre d'un import, el mateix que el backend (`MAX_AMOUNT_CENTS`, `payments.core.ts`): 999.999,99 €.
 * No és cap regla nova: per sobre, el backend ja rebutja la petició.
 */
export const MAX_ORDER_AMOUNT_CENTS = 99_999_999;

/**
 * L'avís (no un error) d'un import per sobre del pendent de la seva reserva. En Pere, 14-OCT-2026:
 * l'import que s'ha dit a l'hoste (p. ex. 75 € per a 74,98 €) és el que s'ha de cobrar; surt en
 * vermell però no impedeix crear l'enllaç. `null` si no passa del pendent o no es pot llegir.
 */
export function overOutstandingWarning(
  amount: string | undefined,
  outstandingCents: number | undefined,
): string | null {
  if (outstandingCents === undefined) return null;
  const cents = eurosTextToCents(amount);
  if (cents === null || cents <= outstandingCents || cents > MAX_ORDER_AMOUNT_CENTS) return null;
  return `Més que el pendent (${formatCents(outstandingCents)}). Es cobrarà l'import que hi posis.`;
}

/**
 * L'esquema del diàleg, fet a mida de la proposta: cada import de 0,01 € com a mínim (pot passar
 * del pendent: només és un avís, `overOutstandingWarning`), almenys una reserva, el total per
 * sobre del mínim i una data màxima futura.
 */
export function buildCreateOrderSchema(preview: PaymentOrderPreview, now: () => Date = () => new Date()) {
  const outstanding = new Map(
    (preview.proposal?.lines ?? []).map((l) => [l.reservationId, l.outstandingCents]),
  );
  return z
    .object({
      lines: z.array(
        z.object({ reservationId: z.string(), selected: z.boolean(), amount: z.string() }),
      ),
      noDue: z.boolean(),
      dueAt: z.string(),
    })
    .superRefine((values, ctx) => {
      let selected = 0;
      let total = 0;
      values.lines.forEach((line, index) => {
        if (!line.selected) return;
        selected += 1;
        const issue = (message: string) =>
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lines', index, 'amount'], message });
        if (!outstanding.has(line.reservationId)) return issue('Aquesta reserva no es pot cobrar');
        const cents = eurosTextToCents(line.amount);
        if (cents === null) return issue('Escriu un import amb dos decimals com a molt');
        if (cents < 1) return issue('Mínim 0,01 €');
        if (cents > MAX_ORDER_AMOUNT_CENTS) {
          return issue(`Màxim ${formatCents(MAX_ORDER_AMOUNT_CENTS)}`);
        }
        total += cents;
      });
      if (selected === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines'],
          message: 'Cal triar almenys una reserva',
        });
      } else if (total > 0 && total < preview.minAmountCents) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines'],
          message: `El total ha de ser de ${formatCents(preview.minAmountCents)} com a mínim`,
        });
      } else if (total > MAX_ORDER_AMOUNT_CENTS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines'],
          message: `El total ha de ser de ${formatCents(MAX_ORDER_AMOUNT_CENTS)} com a màxim`,
        });
      }
      if (!values.noDue) {
        const iso = localInputToIso(values.dueAt);
        if (!iso) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dueAt'],
            message: 'Posa una data o marca «sense data»',
          });
        } else if (new Date(iso).getTime() <= now().getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dueAt'],
            message: 'La data màxima ha de ser futura',
          });
        }
      }
    });
}

/**
 * El cos de `POST /payments/orders`. Sempre amb `lines` explícites: es crea exactament el que la
 * persona ha vist. `dueAt`: absent si no s'ha tocat la data per defecte, `null` si «sense data».
 */
export function buildCreateOrderBody(
  preview: PaymentOrderPreview,
  values: CreateOrderFormValues,
): CreatePaymentOrderInput {
  // El pendent que s'ha vist de cada reserva: si ha canviat entremig, el backend nega amb
  // `TOTAL_CHANGED` (amb un import per sobre del pendent, si no, es cobraria de més sense veure-ho).
  const seen = new Map(
    (preview.proposal?.lines ?? []).map((l) => [l.reservationId, l.outstandingCents]),
  );
  const body: CreatePaymentOrderInput = {
    reservationId: preview.reservationId,
    lines: values.lines
      .filter((l) => l.selected)
      .map((l) => {
        const seenOutstandingCents = seen.get(l.reservationId);
        return {
          reservationId: l.reservationId,
          amountCents: eurosTextToCents(l.amount) ?? 0,
          ...(seenOutstandingCents === undefined ? {} : { seenOutstandingCents }),
        };
      }),
  };
  // El total que la persona veu: si el pendent ha canviat entremig, el backend nega amb
  // `TOTAL_CHANGED` i no crea res.
  body.expectedTotalCents = (body.lines ?? []).reduce((sum, l) => sum + l.amountCents, 0);
  if (values.noDue) {
    body.dueAt = null;
  } else if (values.dueAt !== isoToLocalInput(preview.proposal?.dueAt)) {
    const iso = localInputToIso(values.dueAt);
    if (iso) body.dueAt = iso;
  }
  return body;
}
